import {
  EFinancingCompany,
  EFinancingProgram,
  ELoanPurpose,
  EPaymentFrequency,
  EProvince,
  ETax,
  getEProvinceList,
} from '@if/core/types'
import { getEnvRuntimeVariable } from '@if/core/utils'
import { PaletteColorOptions } from '@mui/material'
import { blue } from '@mui/material/colors'

export const useDevTools = getEnvRuntimeVariable<boolean>(import.meta.env.VITE_USE_DEV_TOOLS)

export const HOME_PAGE = '/'
export const PREFERS_MODE_SETTING = 'themeMode'
export const EARLIER_LUCAS_DATE = '2023-01-01'

export const CLEAR_CACHE_EXCEPTION_KEY = [PREFERS_MODE_SETTING]

export enum EThemeMode {
  Dark = 'dark',
  Light = 'light',
}

export enum EMask {
  phoneNumberMask = '[1-]000-000-0000',
  sinMask = '000 000 000',
  postalCode = 'a0a 0a0',
}

export enum ECurrentCustomerStep {
  Draft = 'draft',
  Prequal = 'prequal',
  Credit = 'credit',
  IncomeVerification = 'incomeVerification',
  Contract = 'contract',
  Funding = 'funding',
}

export enum EPayoutRecoveryMethod {
  Retention = 'retention',
  Chargeback = 'chargeback',
}

export const FederalTaxIds: ETax[] = [ETax.GST, ETax.PST, ETax.HST]

export enum MerchantId {
  IfinanceMerchantId = 'alis*20500',
  MedicardMerchantId = 'alis*20878',
  PayCustomerByBankTransfer = 'alis*451',
  PayCustomerByCheque = 'alis*286',
}

export enum ETerms {
  twelveMonths = 12,
  eighteenMonths = 18,
  twentyFourMonths = 24,
  thirtyMonths = 30,
  thirtySixMonths = 36,
  fortyTwoMonths = 42,
  fortyEightMonths = 48,
  fiftyFourMonths = 54,
  sixtyMonths = 60,
  sixtySixMonths = 66,
  seventyTwoMonths = 72,
  eightyFourMonths = 84,
}

export enum EPersonalLoanCalculatorCreditAppId {
  calculatorId = 'calculator',
}


export const ETermsList: ETerms[] = Object.values(ETerms).filter((value) => typeof value === 'number') as ETerms[]

export const HiddenMerchantCategories = [ELoanPurpose.Dentistry, ELoanPurpose.Medical, ELoanPurpose.Aesthetics]

export interface FinancingProgramConfig {
  readonly maxFirstPaymentDelayInDays: number
  readonly useSoftHit: boolean
  readonly useHardHit: boolean
  readonly beneficiaryIsRequired: boolean
  readonly requestedLoanAmountIsRequired: boolean
  readonly loanPurposeIdIsRequired: boolean
  readonly useHardHitForSpecificProvince: EProvince[] | null
  readonly useAlwaysSubmit: boolean
  readonly displayPrequalify: boolean
  readonly displayIncomeVerification: boolean
  readonly minIncome: number
  readonly minCombinedIncome: number
  readonly minLoanAmount: number
  readonly maxLoanAmount: number
  readonly maxLoanAmountMedicard: number
  readonly frequencyPayments: EPaymentFrequency[]
  readonly supportedProvinces: EProvince[]
  readonly useJobAddress: boolean
  readonly useMaxPayment: boolean
  readonly requiresCreditStepIntervention: boolean
  readonly displayAmountGrantedCreditTile: boolean
  readonly minTerm: ETerms
  readonly maxTerm: ETerms
  readonly termList: ETerms[]
  readonly allowCoappAfterDecline: boolean
  readonly supportsCoApplicant: boolean
  readonly supportsSinglePayment: boolean
  readonly mustProvideWarrantyContract?: boolean
  readonly worksheetIsNeededForCredit: boolean
  readonly supportsInviteMessages: boolean
  readonly shouldDisplayBankStatementDocument: boolean
  readonly supportsContractUpdateOnExpiry: boolean
}

export interface CompanyThemeModeOptions {
  logo: string
  logoContrast: string
  logoAlt: string
  favicon: string
  primary: PaletteColorOptions
  secondary: PaletteColorOptions
}

export interface FinancingCompanyConfigOptions {
  name: string
  supportedPrograms: EFinancingProgram[]
  privacyPolicyLinkEN: string
  privacyPolicyLinkFR: string
  phone: string
  theme: Record<EThemeMode, CompanyThemeModeOptions>
}

export const DefaultProgramConfig: FinancingProgramConfig = {
  maxFirstPaymentDelayInDays: 30,
  useSoftHit: false,
  useHardHit: false,
  beneficiaryIsRequired: false,
  requestedLoanAmountIsRequired: false,
  loanPurposeIdIsRequired: false,
  useHardHitForSpecificProvince: null,
  useAlwaysSubmit: false,
  minIncome: 21000,
  minCombinedIncome: 35000,
  minLoanAmount: 500,
  maxLoanAmount: 40000,
  maxLoanAmountMedicard: 50000,
  displayIncomeVerification: true,
  displayPrequalify: true,
  frequencyPayments: [EPaymentFrequency.Monthly],
  supportedProvinces: getEProvinceList(),
  useJobAddress: true,
  useMaxPayment: true,
  requiresCreditStepIntervention: false,
  minTerm: 12,
  maxTerm: 84,
  termList: ETermsList,
  allowCoappAfterDecline: true,
  displayAmountGrantedCreditTile: true,
  supportsCoApplicant: true,
  supportsSinglePayment: false,
  worksheetIsNeededForCredit: true,
  supportsInviteMessages: false,
  shouldDisplayBankStatementDocument: true,
  supportsContractUpdateOnExpiry: false,
}

export const FinancingProgramConfigs: Record<EFinancingProgram, FinancingProgramConfig> = {
  [EFinancingProgram.Auto]: {
    ...DefaultProgramConfig,
    useHardHit: true,
    frequencyPayments: [EPaymentFrequency.Monthly],
  },
  [EFinancingProgram.Products]: {
    ...DefaultProgramConfig,
    allowCoappAfterDecline: false,
    minCombinedIncome: 0,
    minIncome: 0,
    useAlwaysSubmit: true,
    useHardHitForSpecificProvince: [EProvince.Quebec],
    displayPrequalify: false,
    displayIncomeVerification: false,
    frequencyPayments: [EPaymentFrequency.Monthly, EPaymentFrequency.BiWeekly],
    supportedProvinces: DefaultProgramConfig.supportedProvinces.filter(
      (p) => p !== EProvince.Saskatchewan && p !== EProvince.Manitoba,
    ),
    useJobAddress: false,
    useMaxPayment: false,
    requiresCreditStepIntervention: true,
    minLoanAmount: 750,
    maxTerm: 72,
    termList: ETermsList.filter((term) => term <= ETerms.seventyTwoMonths),
  },
  [EFinancingProgram.Personal]: {
    ...DefaultProgramConfig,
    maxFirstPaymentDelayInDays: 45,
    useSoftHit: true,
    useHardHit: true,
    beneficiaryIsRequired: true,
    requestedLoanAmountIsRequired: true,
    loanPurposeIdIsRequired: true,
    maxTerm: 72,
    frequencyPayments: [EPaymentFrequency.Monthly],
    supportsInviteMessages: true,
    supportsContractUpdateOnExpiry: true,
  },
  [EFinancingProgram.IfXpress]: {
    ...DefaultProgramConfig,
    useSoftHit: true,
    useHardHit: true,
    requestedLoanAmountIsRequired: true,
  },
  [EFinancingProgram.MechanicalWarranty]: {
    ...DefaultProgramConfig,
    minCombinedIncome: 0,
    useAlwaysSubmit: true,
    useHardHitForSpecificProvince: [EProvince.Quebec],
    displayPrequalify: false,
    displayIncomeVerification: false,
    frequencyPayments: [EPaymentFrequency.Single, EPaymentFrequency.Monthly, EPaymentFrequency.BiWeekly],
    supportedProvinces: DefaultProgramConfig.supportedProvinces.filter(
      (p) => p !== EProvince.Saskatchewan && p !== EProvince.Manitoba,
    ),
    useJobAddress: false,
    useMaxPayment: false,
    requiresCreditStepIntervention: true,
    minLoanAmount: 750,
    displayAmountGrantedCreditTile: false,
    maxTerm: ETerms.thirtySixMonths,
    termList: [ETerms.twelveMonths, ETerms.twentyFourMonths, ETerms.thirtySixMonths],
    supportsCoApplicant: false,
    mustProvideWarrantyContract: true,
    supportsSinglePayment: true,
    worksheetIsNeededForCredit: false,
    supportsInviteMessages: true,
    shouldDisplayBankStatementDocument: false,
  },
  [EFinancingProgram.B2c]: DefaultProgramConfig,
  [EFinancingProgram.CreditRsla]: DefaultProgramConfig,
  [EFinancingProgram.AutoRsla]: DefaultProgramConfig,
}

export const FinancingCompanyConfigs: Record<EFinancingCompany, FinancingCompanyConfigOptions> = {
  [EFinancingCompany.Iceberg]: {
    name: 'Iceberg Finance',
    supportedPrograms: [
      EFinancingProgram.Auto,
      EFinancingProgram.Products,
      EFinancingProgram.MechanicalWarranty,
      EFinancingProgram.IfXpress,
    ],
    privacyPolicyLinkEN: 'https://icebergfinance.ca/en/privacy-policy',
    privacyPolicyLinkFR: 'https://icebergfinance.ca/politique-de-confidentialite',
    phone: '1-855-694-0960',
    theme: {
      [EThemeMode.Light]: {
        logo: '/images/iceberg.svg',
        logoContrast: '/images/iceberg-white.svg',
        logoAlt: 'Iceberg Finance',
        favicon: '/images/favicon-iceberg.png',
        primary: {
          main: '#988c60',
        },
        secondary: {
          main: '#131212',
        },
      },
      [EThemeMode.Dark]: {
        logo: '/images/iceberg-white.svg',
        logoContrast: '/images/iceberg-white.svg',
        logoAlt: 'Iceberg Finance',
        favicon: '/images/favicon-iceberg.png',
        primary: {
          main: '#988c60',
        },
        secondary: {
          main: '#131212',
        },
      },
    },
  },
  [EFinancingCompany.IFinance]: {
    name: 'iFinance Canada',
    supportedPrograms: [EFinancingProgram.Personal, EFinancingProgram.IfXpress],
    privacyPolicyLinkEN: 'https://ifinancecanada.com/privacy-policy',
    privacyPolicyLinkFR: 'https://ifinancecanada.com/fr/politique-de-confidentialite',
    phone: '1-888-689-9876',
    theme: {
      [EThemeMode.Light]: {
        logo: '/images/iFinance.svg',
        logoContrast: '/images/iFinance-white.svg',
        logoAlt: 'iFinance',
        favicon: '/images/favicon-ifinance.png',
        primary: {
          main: blue[700],
        },
        secondary: {
          main: '#14233a',
        },
      },
      [EThemeMode.Dark]: {
        logo: '/images/iFinance-white.svg',
        logoContrast: '/images/iFinance-white.svg',
        logoAlt: 'iFinance',
        favicon: '/images/favicon-ifinance.png',
        primary: {
          main: blue[200],
        },
        secondary: {
          main: '#14233a',
        },
      },
    },
  },
}

export const Constants = {
  SystemUserId: 'system',
  LargeLoanAmount: 10000,
  LongTerm: 84,
  SupportedDocumentTypes: '.jpg, .jpeg, .png, .pdf, .heic, .heif',
  SupportedDocumentMimeTypes: ['image/jpeg', 'image/png', 'application/pdf', 'image/heic', 'image/heif'],
  MaxYearsToDisplay: 125,
  AmountRequestedDebounceMs: 500,
}

export const PersonalLoanCalculatorConstants = {
  DEFAULT_AMOUNT: 500,
  BUSINESS_DAYS_OFFSET: 5,
  MAX_PAYMENT_AMOUNT: 5000,
  CORRESPONDENCE_LANGUAGE_ID: 1,
  TEMP_REQUEST_ID: 'calculator-temp-request',
  STICKY_TOP_OFFSET: 20,
  LOADING_SPINNER_SIZE: 40,
} as const

export const PersonalLoanCalculatorGridSizes = {
  COMMON: { xs: 4, md: 4 },
  // 7 credit-rating tiles: 4 per row on desktop so the layout is 4 + 3 (no orphan row)
  CREDIT_RATING: { xs: 6, md: 3 },
  FORM_FIELD: { xs: 6, md: 4 },
  MAIN_CONTENT: { xs: 12, md: 8 },
  PAYMENT_CARD: { xs: 12, md: 4 },
} as const

export interface PersonalLoanCalculatorTermOption {
  label: string
  value: number
}

export interface PersonalLoanCalculatorPaymentOption {
  label: string
  value: string
  id: string
}

export enum EPermissions {
  HasDetailAccess = 'HasDetailAccess',
  CanCreateApplications = 'CanCreateApplications',
  IsAdmin = 'IsAdmin',
  CanSendWelcomeMessage = 'CanSendWelcomeMessage',
}

export enum ECreditRatingLabel {
  VeryExceptional = 'veryExceptional',
  Exceptional = 'exceptional',
  VeryGood = 'veryGood',
  Good = 'good',
  Fair = 'fair',
  Limited = 'limited',
  VeryLimited = 'veryLimited',
}

export interface CreditRatingOption {
  label: ECreditRatingLabel
  range: string
  interestRate: number
}

export const CreditRatingOptions: CreditRatingOption[] = [
  { label: ECreditRatingLabel.VeryExceptional, range: '830+', interestRate: 9.99 },
  { label: ECreditRatingLabel.Exceptional, range: '720-830', interestRate: 12.99 },
  { label: ECreditRatingLabel.VeryGood, range: '700-720', interestRate: 13.99 },
  { label: ECreditRatingLabel.Good, range: '665-700', interestRate: 15.99 },
  { label: ECreditRatingLabel.Fair, range: '635-665', interestRate: 18.99 },
  { label: ECreditRatingLabel.Limited, range: '605-635', interestRate: 20.99 },
  { label: ECreditRatingLabel.VeryLimited, range: '550-605', interestRate: 21.99 },
]

// iFinance promotion: eligible verticals get a 7.95% rate on the top ERS tier (850+),
// mirroring PostDecisionEffectsService.GetIFinancePromotionRate on the backend.
export const IFINANCE_PROMO_VERTICALS: ELoanPurpose[] = [
  ELoanPurpose.Medical,
  ELoanPurpose.Aesthetics,
  ELoanPurpose.Dentistry,
  ELoanPurpose.Veterinary,
]

export const IFINANCE_PROMO_RATE = 7.95
export const IFINANCE_PROMO_TOP_RANGE = '850+'
export const IFINANCE_PROMO_EXCEPTIONAL_RANGE = '720-850'
